import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * /shared-invite/:token
 * Gate 2 · S1 (2026-02) — minimum safe landing for the homeowner→painter
 * invitation email CTA. Directs approved active Pro painters onward, and
 * guides everyone else through Join ColourMatch Pro while retaining the
 * token for the S5 post-approval replay.
 */
const KEY = "cm.invite_token";

function stashToken(token) {
  try { window.sessionStorage.setItem(KEY, token); } catch { /* noop */ }
}

export default function SharedInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  usePageMeta({
    title: "Shared project — ColourMatch",
    description: "A homeowner has shared their ColourMatch project with you.",
    path: `/shared-invite/${token}`,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/shared-invite/${token}`);
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const label = status === 404 ? "This invitation link is invalid or has expired."
                    : status === 410 ? "This invitation has been revoked by the homeowner."
                    : (detail || "We couldn't open this invitation.");
        if (!cancelled) setState({ loading: false, error: label, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const goSignIn = () => {
    stashToken(token);
    nav(`/login?from=/shared-invite/${token}`);
  };

  const goJoinPro = () => {
    stashToken(token);
    nav("/painters/join");
  };

  if (state.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-brand-muted" data-testid="shared-invite-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invitation…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-[60vh] bg-brand-bg flex items-center justify-center px-5">
        <div className="max-w-md text-center" data-testid="shared-invite-error">
          <h1 className="text-2xl font-extrabold text-brand-ink mb-3">Invitation unavailable</h1>
          <p className="text-brand-muted mb-6">{state.error}</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all">
            Back to homepage <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const { homeowner_name, painter_status, painter_email } = state.data;
  const isSignedIn = !!user && user !== false;
  // Signed-in approved-active Pro painter with matching email → straight into workspace.
  // Full ingestion into /pro/leads is S4; for S1 we route them into the dashboard where
  // Stage 2B's pill / inbox will consume the token in the next step.
  const approvedActive = painter_status === "approved_active";
  const emailMatch = isSignedIn && (user?.email || "").toLowerCase() === (painter_email || "").toLowerCase();

  return (
    <div className="min-h-[70vh] bg-brand-bg py-14 lg:py-20 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8" data-testid="shared-invite-header">
          <span className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full">
            <ShieldCheck size={13} /> ColourMatch invitation
          </span>
          <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink leading-tight">
            {homeowner_name} shared their colour ideas with you
          </h1>
          <p className="mt-3 text-brand-muted">
            To open the shared project you&rsquo;ll need an active ColourMatch Pro account.
          </p>
        </div>

        <div className="bg-white border border-brand-border rounded-3xl p-7">
          {approvedActive && emailMatch && (
            <div data-testid="shared-invite-approved">
              <p className="text-sm text-brand-ink mb-4">
                You&rsquo;re signed in as an approved ColourMatch Pro member. Head to your Leads area — the shared project will appear there.
              </p>
              <Link to="/pro/leads" data-testid="shared-invite-go-leads" className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90">
                Open Leads <ArrowRight size={16} />
              </Link>
            </div>
          )}

          {!approvedActive && (
            <div data-testid="shared-invite-not-approved">
              <p className="text-sm text-brand-ink mb-4">
                {painter_status === "approved_inactive"
                  ? "Your ColourMatch Pro membership isn't currently active. Reactivate your membership to open shared projects."
                  : painter_status === "registered_not_approved"
                  ? "Your ColourMatch Pro account is under review. You'll be able to open the shared project once you're approved."
                  : "This invitation is for a ColourMatch Pro painter. Join ColourMatch Pro and once you're approved, the shared project will be waiting in your Leads area."}
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={goJoinPro} data-testid="shared-invite-join-pro" className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90">
                  Join ColourMatch Pro <ArrowRight size={16} />
                </button>
                <button type="button" onClick={goSignIn} data-testid="shared-invite-sign-in" className="inline-flex items-center gap-2 border border-brand-navy text-brand-navy h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-sand">
                  Sign in
                </button>
              </div>
            </div>
          )}

          {approvedActive && !emailMatch && (
            <div data-testid="shared-invite-sign-in-required">
              <p className="text-sm text-brand-ink mb-4">
                Please sign in with the ColourMatch Pro account that received this invitation ({painter_email}) to open the shared project.
              </p>
              <button type="button" onClick={goSignIn} className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90">
                Sign in <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
